/**
 * StudySync Researcher Authentication Component
 * Login and signup forms for researchers
 */

import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Tabs,
  Tab,
  Divider,
  Link,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
} from "@mui/material";
import {
  Google,
  Science,
  Email,
  Lock,
  Person,
  Visibility,
  VisibilityOff,
  Security,
  Phone,
} from "@mui/icons-material";
import { formatPhoneNumber } from "../../utils/textUtils";
import { useSecureAuth } from "../../contexts/SecureAuthContext";
import { useUser } from "../../contexts/UserContext";
import { useNavigate, useLocation } from "react-router-dom";
import LoadingSpinner from "../common/LoadingSpinner";
import { analyticsService } from "../../services/analyticsService";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

// Module-level variable to persist across component remounts (StrictMode)
// This is necessary because React.StrictMode causes remounts, which reset refs
let pendingErrorMessageModule: string = "";

const ResearcherAuth: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser, isLoading: authLoading, login, signInWithGoogle } = useSecureAuth();
  const { userProfile, loading: userLoading, isMasterAccount, isApprovedResearcher } = useUser();

  // All hooks must be called before any conditional returns
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  
  // Form states
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");

  // 2FA states
  const [show2FA, setShow2FA] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [sessionId, setSessionId] = useState("");

  // Password visibility states
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);

  // Success popup state
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  
  // Password error dialog state
  const [showPasswordErrorDialog, setShowPasswordErrorDialog] = useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = useState("");
  
  // Use ref for component-level state, but also use module-level variable as backup
  const pendingErrorMessageRef = useRef("");
  
  // Restore from module if ref is empty but module has value (remount scenario)
  useEffect(() => {
    if (!pendingErrorMessageRef.current && pendingErrorMessageModule) {
      pendingErrorMessageRef.current = pendingErrorMessageModule;
    }
  }, []);

  // Use useLayoutEffect to restore dialog state synchronously if it was cleared
  useLayoutEffect(() => {
    // Check BOTH ref and module-level variable (module persists across remounts)
    const refMessage = pendingErrorMessageRef.current || pendingErrorMessageModule;
    
    // If we have a pending message but dialog is closed, restore it
    if (!showPasswordErrorDialog && refMessage) {
      // Restore ref from module if needed
      if (!pendingErrorMessageRef.current && pendingErrorMessageModule) {
        pendingErrorMessageRef.current = pendingErrorMessageModule;
      }
      // Use the message directly
      setPasswordErrorMessage(refMessage);
      setShowPasswordErrorDialog(true);
      return; // Exit early to prevent clearing
    }
    
    // Only clear ref AND module once dialog is successfully open AND we have a message
    if (showPasswordErrorDialog && passwordErrorMessage) {
      pendingErrorMessageRef.current = "";
      pendingErrorMessageModule = "";
    }
  }, [showPasswordErrorDialog, passwordErrorMessage]);

  // Store current URL params if coming from a deeplink
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const redirect = searchParams.get("redirect");
    if (redirect) {
      sessionStorage.setItem("redirectAfterLogin", redirect);
    }
  }, []);

  // Redirect authenticated users away from auth page
  useEffect(() => {
    if (authLoading || userLoading) {
      return; // Still loading, wait
    }

    if (currentUser) {
      // A support notification should return to that ticket after sign-in.
      // Accept only our two exact local routes, never an arbitrary redirect URL.
      const supportReturn = sessionStorage.getItem('redirectAfterLogin');
      if (supportReturn && /^\/(?:master\/dashboard)?\?supportTicket=SUP-[A-F0-9]{20}$/.test(supportReturn)) {
        sessionStorage.removeItem('redirectAfterLogin');
        navigate(supportReturn, { replace: true });
        return;
      }
      // User is authenticated - redirect based on status
      if (isMasterAccount) {
        navigate('/master/dashboard', { replace: true });
        return;
      }

      if (isApprovedResearcher) {
        navigate('/researcher/dashboard', { replace: true });
        return;
      }

      // Check user profile status
      if (userProfile) {
        if (userProfile.status === 'pending' || userProfile.status === 'rejected') {
          navigate('/researcher/application-status', { replace: true });
          return;
        }

        if (userProfile.status === 'noprofile') {
          navigate('/researcher/register', { replace: true });
          return;
        }
      }

      // Default: redirect to dashboard
      navigate('/dashboard', { replace: true });
    }
  }, [currentUser, authLoading, userLoading, userProfile, isMasterAccount, isApprovedResearcher, navigate]);

  // Show loading spinner while checking auth status
  // BUT: Don't return early if we're showing a password error dialog
  if ((authLoading || userLoading) && !showPasswordErrorDialog) {
    return <LoadingSpinner />;
  }

  // If authenticated, don't render auth page (redirect will happen)
  // BUT: Don't return early if we're showing a password error dialog
  if (currentUser && !showPasswordErrorDialog) {
    return <LoadingSpinner />;
  }

  // Get the redirect URL from location state or stored in sessionStorage
  const getRedirectUrl = () => {
    // For approved researchers, always go to dashboard (SmartRedirect will handle routing)
    // Only use stored redirect for non-researcher routes or special cases
    const storedRedirect = sessionStorage.getItem("redirectAfterLogin");
    
    // If there's a stored redirect, check if it's a researcher study route
    // If so, ignore it and go to dashboard instead
    if (storedRedirect && storedRedirect.startsWith("/researcher/study/")) {
      sessionStorage.removeItem("redirectAfterLogin");
      return "/dashboard";
    }
    
    // Check location state first (passed from protected route)
    if (location.state && location.state.from) {
      const fromPath = location.state.from;
      // If it's a study route, redirect to dashboard instead
      if (fromPath.startsWith("/researcher/study/")) {
        return "/dashboard";
      }
      return fromPath;
    }
    
    // Use stored redirect for non-study routes (like participant routes, etc.)
    if (storedRedirect) {
      return storedRedirect;
    }
    
    // Default to dashboard (SmartRedirect will route appropriately)
    return "/dashboard";
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setError("");
    setMessage("");
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      setError("Please fill in all fields");
      return;
    }

    try {
      setError("");
      setLoading(true);
      
      await login(email, password);
      analyticsService.trackSignIn('email');

      // Get the redirect URL and clear sessionStorage
      const redirectUrl = getRedirectUrl();
      sessionStorage.removeItem("redirectAfterLogin");

      // Navigate to the intended destination
      navigate(redirectUrl);
    } catch (error: any) {
      const errorMessage = error?.message || error?.toString() || "Unknown error";
      
      // Check if it's a password/authentication error
      const lowerErrorMessage = errorMessage.toLowerCase();
      
      const isAuthError = lowerErrorMessage.includes('invalid') || 
                         lowerErrorMessage.includes('password') ||
                         lowerErrorMessage.includes('email') ||
                         lowerErrorMessage.includes('authentication') ||
                         lowerErrorMessage.includes('credentials');
      
      if (isAuthError) {
        // Show popup dialog for password errors
        const dialogMessage = errorMessage.includes('Invalid email or password') 
          ? "The password you entered is incorrect. Please try again."
          : errorMessage.includes('Invalid email')
          ? "The email address you entered is incorrect. Please check and try again."
          : "The email or password you entered is incorrect. Please try again.";
        
        // Store message in BOTH ref AND module-level variable
        // Module-level persists across remounts (StrictMode)
        pendingErrorMessageRef.current = dialogMessage;
        pendingErrorMessageModule = dialogMessage;
        
        // Set both states together - React will batch these
        setPasswordErrorMessage(dialogMessage);
        setShowPasswordErrorDialog(true);
        
        setError(""); // Clear inline error since we're showing dialog
      } else {
        // Show inline error for other errors
        setError("Failed to sign in: " + errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !email ||
      !password ||
      !confirmPassword ||
      !displayName ||
      !phoneNumber
    ) {
      setError("Please fill in all fields");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    try {
      setError("");
      setLoading(true);

      // First, create the account via direct API call to get the user ID
      const registerResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/api/auth/register`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email,
            password,
            displayName,
            phoneNumber,
          }),
        }
      );

      const registerData = await registerResponse.json();
      if (!registerData.success) {
        throw new Error(registerData.message || "Registration failed");
      }

      // Store the partial account ID for verification
      localStorage.setItem("pending_user_id", registerData.user.uid);

      // Then initiate SMS verification
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/secure-auth/initiate-verification`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email,
            method: "sms",
          }),
        }
      );

      const data = await response.json();
      if (data.success) {
        setSessionId(data.sessionId);
        setShow2FA(true);
        setMessage("Verification code sent to your phone");
      } else {
        throw new Error(data.error || "Failed to send verification code");
      }
    } catch (error: any) {
      setError(
        "Failed to create account: " + (error.message || "Unknown error")
      );
    } finally {
      setLoading(false);
    }
  };

  const handle2FAVerification = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!verificationCode || verificationCode.length !== 6) {
      setError("Please enter a valid 6-digit verification code");
      return;
    }

    try {
      setError("");
      setLoading(true);

      const userId = localStorage.getItem("pending_user_id");

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/secure-auth/verify-code`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            sessionId,
            verificationCode,
            method: "sms",
            userId,
          }),
        }
      );

      const data = await response.json();
      if (data.success) {
        // Clean up localStorage
        localStorage.removeItem("pending_user_id");
        analyticsService.trackSignUp('email');
        // Show success popup
        setShowSuccessPopup(true);
      } else {
        throw new Error(data.error || "Verification failed");
      }
    } catch (error: any) {
      setError("Verification failed: " + (error.message || "Unknown error"));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setError("");
      setLoading(true);

      await signInWithGoogle();
      analyticsService.trackSignIn('google');

      // Get the redirect URL and clear sessionStorage
      const redirectUrl = getRedirectUrl();
      sessionStorage.removeItem("redirectAfterLogin");

      // Navigate to the intended destination
      navigate(redirectUrl);
    } catch (error: any) {
      setError(
        "Failed to sign in with Google: " + (error.message || "Unknown error")
      );
    } finally {
      setLoading(false);
    }
  };


  if (loading) {
    return <LoadingSpinner message="Authenticating..." fullScreen />;
  }

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          {/* Header */}
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Science sx={{ fontSize: 48, color: "primary.main", mb: 2 }} />
            <Typography
              variant="h4"
              component="h1"
              sx={{ fontWeight: 600, mb: 1 }}
            >
              Researcher Portal
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sign in to manage your research studies
            </Typography>
          </Box>

          {/* Error/Success Messages */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {message && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {message}
            </Alert>
          )}

          {/* Google Sign In */}
          <Button
            fullWidth
            variant="outlined"
            startIcon={<Google />}
            onClick={handleGoogleSignIn}
            sx={{ mb: 3, py: 1.5 }}
          >
            Continue with Google
          </Button>

          <Divider sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary">
              or
            </Typography>
          </Divider>

          {/* Tabs */}
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            centered
            sx={{ mb: 2 }}
          >
            <Tab label="Sign In" />
            <Tab label="Sign Up" />
          </Tabs>

          {/* Sign In Form */}
          <TabPanel value={tabValue} index={0}>
            <Box component="form" onSubmit={handleLogin}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                margin="normal"
                required
                InputProps={{
                  startAdornment: (
                    <Email sx={{ mr: 1, color: "text.secondary" }} />
                  ),
                }}
              />
              <TextField
                fullWidth
                label="Password"
                type={showLoginPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                margin="normal"
                required
                InputProps={{
                  startAdornment: (
                    <Lock sx={{ mr: 1, color: "text.secondary" }} />
                  ),
                  endAdornment: (
                    <IconButton
                      onClick={() => setShowLoginPassword(!showLoginPassword)}
                      edge="end"
                    >
                      {showLoginPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  ),
                }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2, py: 1.5 }}
                disabled={loading}
              >
                Sign In
              </Button>
              <Box sx={{ textAlign: "center" }}>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => navigate("/researcher/forgot-password")}
                  sx={{ cursor: "pointer" }}
                >
                  Forgot your password?
                </Link>
              </Box>
            </Box>
          </TabPanel>

          {/* Sign Up Form */}
          <TabPanel value={tabValue} index={1}>
            {!show2FA ? (
              <Box component="form" onSubmit={handleSignup}>
                <TextField
                  fullWidth
                  label="Full Name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  margin="normal"
                  required
                  InputProps={{
                    startAdornment: (
                      <Person sx={{ mr: 1, color: "text.secondary" }} />
                    ),
                  }}
                />
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  margin="normal"
                  required
                  InputProps={{
                    startAdornment: (
                      <Email sx={{ mr: 1, color: "text.secondary" }} />
                    ),
                  }}
                />
                <TextField
                  fullWidth
                  label="Phone Number"
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) =>
                    setPhoneNumber(formatPhoneNumber(e.target.value))
                  }
                  margin="normal"
                  required
                  placeholder="(000)-0000-000"
                  helperText="Two-factor authentication available for phone number only."
                  InputProps={{
                    startAdornment: (
                      <Phone sx={{ mr: 1, color: "text.secondary" }} />
                    ),
                    endAdornment: !phoneNumber && (
                      <InputAdornment position="end">
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                          US phone number
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  fullWidth
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  margin="normal"
                  required
                  helperText="Minimum 6 characters"
                  InputProps={{
                    startAdornment: (
                      <Lock sx={{ mr: 1, color: "text.secondary" }} />
                    ),
                    endAdornment: (
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    ),
                  }}
                />
                <TextField
                  fullWidth
                  label="Confirm Password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  margin="normal"
                  required
                  InputProps={{
                    startAdornment: (
                      <Lock sx={{ mr: 1, color: "text.secondary" }} />
                    ),
                    endAdornment: (
                      <IconButton
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        edge="end"
                      >
                        {showConfirmPassword ? (
                          <Visibility />
                        ) : (
                          <VisibilityOff />
                        )}
                      </IconButton>
                    ),
                  }}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2, py: 1.5 }}
                  disabled={loading}
                >
                  Create Account
                </Button>
              </Box>
            ) : (
              <Box component="form" onSubmit={handle2FAVerification}>
                <Box sx={{ textAlign: "center", mb: 3 }}>
                  <Security
                    sx={{ fontSize: 48, color: "primary.main", mb: 2 }}
                  />
                  <Typography variant="h5" gutterBottom>
                    SMS Verification
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    We've sent a 6-digit verification code to your phone
                  </Typography>
                </Box>

                <TextField
                  fullWidth
                  label="Verification Code"
                  value={verificationCode}
                  onChange={(e) =>
                    setVerificationCode(
                      e.target.value.replace(/\D/g, "").slice(0, 6)
                    )
                  }
                  margin="normal"
                  required
                  placeholder="123456"
                  inputProps={{
                    maxLength: 6,
                    style: {
                      textAlign: "center",
                      fontSize: "1.2rem",
                      letterSpacing: "0.5rem",
                    },
                  }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2, py: 1.5 }}
                  disabled={loading || verificationCode.length !== 6}
                >
                  Verify SMS
                </Button>

                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => {
                    setShow2FA(false);
                    setVerificationCode("");
                    setSessionId("");
                  }}
                  sx={{ mb: 2 }}
                >
                  Back to Sign Up
                </Button>
              </Box>
            )}
          </TabPanel>

          {/* Footer */}
          <Box
            sx={{
              textAlign: "center",
              mt: 3,
              pt: 2,
              borderTop: 1,
              borderColor: "divider",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Need help? Contact your research administrator
            </Typography>
          </Box>
        </Paper>
      </Box>

      {/* Success Popup */}
      <Dialog
        open={showSuccessPopup}
        onClose={() => setShowSuccessPopup(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: "center", pt: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                backgroundColor: "success.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Security sx={{ color: "white", fontSize: 32 }} />
            </Box>
          </Box>
          <Typography variant="h5" component="div" sx={{ fontWeight: "bold" }}>
            Account Created Successfully! 🎉
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center", pb: 2 }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            Your researcher account has been created and is now pending
            approval.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You can now log in with your credentials. An administrator will
            review and approve your account.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={() => {
              setShowSuccessPopup(false);
              navigate("/");
            }}
            sx={{ px: 4 }}
          >
            Continue to Login
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Error Dialog */}
      <Dialog
        open={showPasswordErrorDialog || !!pendingErrorMessageRef.current || !!pendingErrorMessageModule}
        onClose={() => {
          setShowPasswordErrorDialog(false);
          pendingErrorMessageRef.current = ""; // Clear ref when closing
          pendingErrorMessageModule = ""; // Clear module when closing
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: "center", pt: 3 }}>
          <Security sx={{ fontSize: 48, color: "error.main", mb: 2 }} />
          <Typography variant="h5" component="div">
            Incorrect Password
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center", pb: 2 }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {passwordErrorMessage || pendingErrorMessageRef.current || pendingErrorMessageModule || "The password you entered is incorrect. Please try again."}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please check your password and try again, or use the "Forgot your password?" link to reset it.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button
            variant="contained"
            onClick={() => {
              setShowPasswordErrorDialog(false);
              setPassword(""); // Clear password field
            }}
            sx={{ minWidth: 120 }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ResearcherAuth;
